import { useCallback } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import {
  useUpdateSupplierBuyerMovMutation,
  useRemoveSupplierBuyerMovMutation,
} from '../../../generated';

interface UseUpdateSupplierBuyerMovConfig {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/** The (supplier entity, buyer) pair a customer-specific MOV is keyed by. */
export interface SupplierBuyerMovKey {
  supplierEntityId: string;
  buyerId: string;
}

export interface UpdateSupplierBuyerMovArgs extends SupplierBuyerMovKey {
  /** Amount in minor units (e.g. cents), matching the backend `amount: Int`. */
  amount: number;
  currency: string;
}

/**
 * Data hook for the chat's customer-specific minimum order value.
 *
 * `updateMov` sets/overwrites the customer-level MOV; `removeMov` clears it so
 * the chat falls back to the supplier-level default. Both are keyed by the
 * distributor entity id + buyer id (mirrors the legacy admin). Result unions
 * are surfaced as toasts here so callers only need to fire and await.
 */
export const useUpdateSupplierBuyerMov = (
  config: UseUpdateSupplierBuyerMovConfig = {}
) => {
  const { onSuccess, onError } = config;
  const { addNotification } = useNotification();

  // Reconcile the chat's resolved MOV after a successful write so the Settings
  // form's load-mapping reflects server truth.
  const mutationOptions = {
    fetchPolicy: 'no-cache' as const,
    errorPolicy: 'all' as const,
    refetchQueries: ['GetChatData'],
  };

  const [runUpdate, { loading: isUpdatingMov }] =
    useUpdateSupplierBuyerMovMutation(mutationOptions);

  const [runRemove, { loading: isRemovingMov }] =
    useRemoveSupplierBuyerMovMutation(mutationOptions);

  const notifyFailureAndThrow = useCallback(
    (message: string): never => {
      const error = new Error(message);
      onError?.(error);
      addNotification({ title: 'Error', message, type: 'error' });
      throw error;
    },
    [addNotification, onError]
  );

  const notifySuccess = useCallback(() => {
    addNotification({
      title: 'Success',
      message: 'Customer minimum order value updated successfully.',
      type: 'success',
    });
    onSuccess?.();
  }, [addNotification, onSuccess]);

  const updateMov = useCallback(
    async ({
      supplierEntityId,
      buyerId,
      amount,
      currency,
    }: UpdateSupplierBuyerMovArgs) => {
      try {
        const result = await runUpdate({
          variables: {
            input: [{ supplierEntityId, buyerId, amount, currency }],
          },
        });
        const graphQLErrors = result.errors;
        if (graphQLErrors && graphQLErrors.length > 0) {
          throw new Error(
            graphQLErrors[0]?.message ||
              'Failed to update customer minimum order value'
          );
        }
        const payload = result.data?.updateSupplierBuyerMov;
        if (payload?.__typename !== 'MinimumOrderValueUpdate') {
          const message =
            payload && 'message' in payload ? payload.message : null;
          throw new Error(
            message || 'Failed to update customer minimum order value'
          );
        }
        notifySuccess();
      } catch (updateError: unknown) {
        notifyFailureAndThrow(
          updateError instanceof Error
            ? updateError.message
            : 'Failed to update customer minimum order value'
        );
      }
    },
    [runUpdate, notifySuccess, notifyFailureAndThrow]
  );

  const removeMov = useCallback(
    async ({ supplierEntityId, buyerId }: SupplierBuyerMovKey) => {
      try {
        const result = await runRemove({
          variables: { input: [{ supplierEntityId, buyerId }] },
        });
        const graphQLErrors = result.errors;
        if (graphQLErrors && graphQLErrors.length > 0) {
          throw new Error(
            graphQLErrors[0]?.message ||
              'Failed to update customer minimum order value'
          );
        }
        const payload = result.data?.removeSupplierBuyerMov;
        if (payload?.__typename !== 'MinimumOrderValueUpdate') {
          const message =
            payload && 'message' in payload ? payload.message : null;
          throw new Error(
            message || 'Failed to update customer minimum order value'
          );
        }
        notifySuccess();
      } catch (removeError: unknown) {
        notifyFailureAndThrow(
          removeError instanceof Error
            ? removeError.message
            : 'Failed to update customer minimum order value'
        );
      }
    },
    [runRemove, notifySuccess, notifyFailureAndThrow]
  );

  return {
    updateMov,
    removeMov,
    isUpdating: isUpdatingMov || isRemovingMov,
  };
};
